import { useMainStore } from "@/store/mainStore";
import { PressJoinCategory, ProcessType, Surface, TaskType } from "./Enums";
import { FeatureType } from "./FeatureType";
import { IFeature } from "./IFeature";
import { IFormingParameter } from "./ParameterTypes";
import { ITaskDto } from "./ITaskDto";

export abstract class FeatureHelper {
	protected feature: IFeature;

	protected constructor(feature: IFeature) {
		this.feature = feature;
	}

	public static create(feature: IFeature): FeatureHelper {
		switch (feature.featureType) {
			case FeatureType.Bend:
				return new BendFeatureHelper(feature);
			case FeatureType.Hole:
				return new HoleFeatureHelper(feature);
			case FeatureType.Cutout:
				return new CutoutFeatureHelper(feature);
			case FeatureType.ScrewThread:
				return new ScrewThreadFeatureHelper(feature);
			case FeatureType.Countersink:
				return new CountersinkFeatureHelper(feature);
			case FeatureType.ExtrudedThread:
				return new ExtrudedThreadFeatureHelper(feature);
			case FeatureType.Engraving:
				return new EngravingFeatureHelper(feature);
			case FeatureType.Invalid:
				return new InvalidFeatureHelper(feature);
			case FeatureType.Weldline:
				return new WeldlineFeatureHelper(feature);
			case FeatureType.CylinderHeadBoltHole:
				return new CylinderHeadBoltHoleFeatureHelper(feature);
			case FeatureType.PressJoin:
				return new PressJoinFeatureHelper(feature);
			case FeatureType.AnchorFace:
			case FeatureType.BottomFace:
			case FeatureType.SideFace:
				return new SideHelper(feature);
			default:
				throw new Error("Unknown feature type");
		}
	}

	public abstract getProcessType(): ProcessType;
	public getPossibleTargetTypes(): ProcessType[] {
		return [];
	}
	public getFormingParameter(): IFormingParameter[] {
		return [];
	}

	public static getFormingParametersByProcessType(processType: ProcessType): IFormingParameter[] {
		const store = useMainStore();
		switch (processType) {
			case ProcessType.CylindicBend:
				return [];
			case ProcessType.Cut:
				return [];
			case ProcessType.FormScrewThread:
				return store.screwThreadParameters;
			case ProcessType.FormCountersink:
				return store.countersinkParameters;
			case ProcessType.DrillCylinderHeadBoltHole:
				return store.cylinderheadBoltHoleParameters;
			case ProcessType.FormExtrudedThread:
				return store.extrudedThreadParameters;
			case ProcessType.PressJoin:
				return store.pressJoinParameter;
			case ProcessType.Engrave:
				return [];
			case ProcessType.Weld:
				return [];
			case ProcessType.None:
				return [];
			default:
				throw new Error("Unknown feature type");
		}
	}

	/**
	 * Gets a task that transforms the current feature to the new target process type.
	 * @param targetProcessType
	 * @param toolId Id of tool used to create the feature
	 */
	public getTaskForTargetProcessType(
		targetProcessType: ProcessType,
		toolId: string,
		surface: Surface
	): ITaskDto | null {
		const store = useMainStore();
		const originalExportIds = store.cam.getOriginalExportIdsForSelectedFeatures(
			this.feature.exportIds
		);
		switch (targetProcessType) {
			case ProcessType.Cut:
				let radius = this.feature.radius;
				if (this.feature.featureType === FeatureType.Countersink) {
					radius = this.feature.innerRadius;
				} else if (this.feature.featureType === FeatureType.ExtrudedThread) {
					radius = this.feature.radius - store.validationParameter.dicke;
				}
				return {
					exportIds: originalExportIds,
					radius: radius,
					innerRadius: 0,
					toolId: "",
					type: TaskType.CreateHole,
					isOnAnchorFace: true,
					newExportIds: [],
				};
			case ProcessType.Engrave:
				return {
					exportIds: originalExportIds,
					radius:
						this.feature.featureType === FeatureType.Countersink
							? this.feature.radius
							: 0,
					innerRadius:
						this.feature.featureType === FeatureType.Countersink
							? this.feature.innerRadius
							: 0,
					toolId: "",
					type:
						this.feature.featureType === FeatureType.Countersink
							? TaskType.EngraveCountersink
							: TaskType.CreateEngraving,
					isOnAnchorFace: true,
					newExportIds: [],
				};
			case ProcessType.FormScrewThread:
				const screwThreadToolParameter = store.screwThreadParameters.find(
					(p) => p.toolId === toolId
				);
				return {
					exportIds: originalExportIds,
					radius: screwThreadToolParameter!.kernlochDurchmesser / 2,
					innerRadius: 0,
					toolId: toolId,
					type: TaskType.CreateScrewThread,
					isOnAnchorFace: true, // Not important
					newExportIds: [],
				};
			case ProcessType.FormCountersink:
				const counterSinkToolParameter = store.countersinkParameters.find(
					(p) => p.toolId === toolId
				);
				return {
					exportIds: originalExportIds,
					radius: counterSinkToolParameter!.senkungsdurchmesser / 2,
					innerRadius: counterSinkToolParameter!.durchgangsdurchmesser / 2,
					toolId: toolId,
					type: TaskType.CreateCountersink,
					isOnAnchorFace: counterSinkToolParameter!.side === Surface.Front,
					newExportIds: [],
				};
			case ProcessType.FormExtrudedThread:
				const extrudedThreadTool = store.extrudedThreadParameters.find(
					(p) => p.toolId === toolId
				);
				return {
					exportIds: originalExportIds,
					radius: extrudedThreadTool!.durchzugAussendurchmesser / 2,
					innerRadius: 0,
					toolId: toolId,
					height: extrudedThreadTool!.durchzugHoehe,
					type: TaskType.CreateExtrudedThread,
					isOnAnchorFace: extrudedThreadTool!.side === Surface.Front,
					newExportIds: [],
				};
			case ProcessType.DrillCylinderHeadBoltHole:
				const cylinderheadBoltHoleParameter = store.cylinderheadBoltHoleParameters.find(
					(p) => p.toolId === toolId
				);
				return {
					exportIds: originalExportIds,
					radius: cylinderheadBoltHoleParameter!.zylinderDurchmesser / 2,
					innerRadius: cylinderheadBoltHoleParameter!.durchgangsDurchmesser / 2,
					toolId: toolId,
					height: cylinderheadBoltHoleParameter?.tiefeDerSenkung,
					type: TaskType.CreateCylinderheadBoltHole,
					isOnAnchorFace: cylinderheadBoltHoleParameter!.side === Surface.Front,
					newExportIds: [],
				};
			case ProcessType.PressJoin:
				const pressJoinParameter = store.pressJoinParameter.find(
					(p) => p.toolId === toolId
				);
				let isOnAnchorFace = surface === Surface.Front;

				if (pressJoinParameter?.category == PressJoinCategory.SelfClinchingThreadedStud ||
					pressJoinParameter?.category === PressJoinCategory.SelfClinchingThreadedStandoff
				) {
					// Bei den obigen Kategorien ist es logischer, wenn wir die drehen
					isOnAnchorFace = !isOnAnchorFace
				}
				return {
					exportIds: originalExportIds,
					radius: pressJoinParameter!.kernlochDurchmesser / 2,
					innerRadius: 0,
					pressJoinCategory: pressJoinParameter!.category,
					toolId: toolId,
					type: TaskType.CreatePressJoin,
					isOnAnchorFace: isOnAnchorFace,
					newExportIds: [],
				};
			default:
				return null;
		}
	}
}

export class BendFeatureHelper extends FeatureHelper {
	public override getProcessType() {
		return ProcessType.CylindicBend;
	}

	public override getPossibleTargetTypes(): ProcessType[] {
		return [ProcessType.CylindicBend];
	}
}

export class HoleFeatureHelper extends FeatureHelper {
	public getProcessType() {
		return ProcessType.Cut;
	}

	public override getPossibleTargetTypes(): ProcessType[] {
		return [
			ProcessType.Cut,
			ProcessType.Engrave,
			ProcessType.FormCountersink,
			ProcessType.FormExtrudedThread,
			ProcessType.FormScrewThread,
			ProcessType.DrillCylinderHeadBoltHole,
			ProcessType.PressJoin,
		];
	}
}

export class CutoutFeatureHelper extends FeatureHelper {
	public getProcessType() {
		return ProcessType.Cut;
	}

	public override getPossibleTargetTypes(): ProcessType[] {
		return [ProcessType.Cut, ProcessType.Engrave];
	}
}

export class ScrewThreadFeatureHelper extends FeatureHelper {
	public getProcessType() {
		return ProcessType.FormScrewThread;
	}

	public override getPossibleTargetTypes(): ProcessType[] {
		return [
			ProcessType.Cut,
			ProcessType.Engrave,
			ProcessType.FormCountersink,
			ProcessType.FormExtrudedThread,
			ProcessType.FormScrewThread,
		];
	}

	public override getFormingParameter(): IFormingParameter[] {
		const store = useMainStore();
		const parameter = store.screwThreadParameters;
		return parameter;
	}
}

export class CountersinkFeatureHelper extends FeatureHelper {
	public getProcessType() {
		return ProcessType.FormCountersink;
	}

	public override getPossibleTargetTypes(): ProcessType[] {
		return [
			ProcessType.Cut,
			ProcessType.Engrave,
			ProcessType.FormScrewThread,
			ProcessType.FormCountersink,
			ProcessType.FormExtrudedThread,
		];
	}

	public override getFormingParameter(): IFormingParameter[] {
		const store = useMainStore();
		const parameter = store.countersinkParameters;
		return parameter;
	}
}

export class CylinderHeadBoltHoleFeatureHelper extends FeatureHelper {
	public getProcessType() {
		return ProcessType.DrillCylinderHeadBoltHole;
	}

	public override getPossibleTargetTypes(): ProcessType[] {
		return [ProcessType.Cut, ProcessType.Engrave, ProcessType.DrillCylinderHeadBoltHole];
	}

	public override getFormingParameter(): IFormingParameter[] {
		const store = useMainStore();
		const parameter = store.countersinkParameters;
		return parameter;
	}
}

export class PressJoinFeatureHelper extends FeatureHelper {
	public getProcessType() {
		return ProcessType.PressJoin;
	}

	public override getPossibleTargetTypes(): ProcessType[] {
		return [ProcessType.Cut, ProcessType.PressJoin];
	}

	public override getFormingParameter(): IFormingParameter[] {
		const store = useMainStore();
		const parameter = store.pressJoinParameter;
		return parameter;
	}
}

export class ExtrudedThreadFeatureHelper extends FeatureHelper {
	public getProcessType() {
		return ProcessType.FormExtrudedThread;
	}

	public override getPossibleTargetTypes(): ProcessType[] {
		return [
			ProcessType.Cut,
			ProcessType.FormExtrudedThread,
			ProcessType.FormCountersink,
			ProcessType.FormScrewThread,
		];
	}

	public override getFormingParameter(): IFormingParameter[] {
		const store = useMainStore();
		const parameter = store.extrudedThreadParameters;
		return parameter;
	}
}

export class EngravingFeatureHelper extends FeatureHelper {
	public getProcessType() {
		return ProcessType.Engrave;
	}

	public override getPossibleTargetTypes(): ProcessType[] {
		return [ProcessType.Engrave];
	}
}

export class InvalidFeatureHelper extends FeatureHelper {
	public getProcessType() {
		return ProcessType.None;
	}

	public override getPossibleTargetTypes(): ProcessType[] {
		return [ProcessType.None];
	}
}

export class WeldlineFeatureHelper extends FeatureHelper {
	public getProcessType() {
		return ProcessType.Weld;
	}

	public override getPossibleTargetTypes(): ProcessType[] {
		return [ProcessType.Weld];
	}
}

export class SideHelper extends FeatureHelper {
	public getProcessType() {
		return ProcessType.None;
	}
}
